if (!customElements.get('collection-banner-popup')) {
  customElements.define(
    'collection-banner-popup',
    class CollectionBannerPopup extends HTMLElement {
      static PLACEHOLDER_IMG =
        'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1 1" preserveAspectRatio="xMidYMid slice"><rect width="1" height="1" fill="%23f3f3f3"/></svg>';

      constructor() {
        super();
        this.triggerButton = null;
        this.sets = [];
        this.activeSetIdx = undefined;
        this.selected = new Set();
        this.copyResetTimer = null;
        this.toastTimer = null;
        this.boundOnKeydown = this.onKeydown.bind(this);
      }

      connectedCallback() {
        // Relocate to <body> to escape any parent stacking context
        // (theme header / sticky-header create their own stacking context with higher z-order
        //  than collection section, so z-index alone is not enough)
        if (this.parentElement && this.parentElement !== document.body) {
          document.body.appendChild(this);
          return; // disconnectedCallback then connectedCallback will fire again with parent=body
        }

        this.boundDocumentClick = (e) => this.handleTriggerClick(e);
        document.addEventListener('click', this.boundDocumentClick);

        this.boundOnClick = (e) => this.onClick(e);
        this.addEventListener('click', this.boundOnClick);

        this.boundOnChange = (e) => this.onChange(e);
        this.addEventListener('change', this.boundOnChange);
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.boundDocumentClick);
        document.removeEventListener('keydown', this.boundOnKeydown);
        if (this.copyResetTimer) clearTimeout(this.copyResetTimer);
        if (this.toastTimer) clearTimeout(this.toastTimer);
      }

      // ---- Event delegation ----

      handleTriggerClick(event) {
        const trigger = event.target.closest('[data-collection-banner-trigger]');
        if (!trigger) return;
        event.preventDefault();
        this.open(trigger);
      }

      onClick(event) {
        if (event.target.closest('[data-popup-discount-target]')) {
          this.copyDiscount();
          return;
        }
        if (event.target.closest('[data-popup-cta-target]')) {
          this.addToCart();
          return;
        }
        if (event.target.closest('[data-popup-set-toggle]')) {
          this.toggleSetDropdown();
          return;
        }
        const setOption = event.target.closest('[data-set-idx]');
        if (setOption) {
          const idx = parseInt(setOption.dataset.setIdx, 10);
          if (Number.isFinite(idx) && idx !== this.activeSetIdx) {
            this.selectSet(idx, true);
          }
          this.closeSetDropdown();
          return;
        }
        // Click outside open dropdown — close it (but don't fall through to popup-close)
        if (this.isSetDropdownOpen() && !event.target.closest('[data-popup-set-dropdown]')) {
          this.closeSetDropdown();
        }
        if (
          event.target.closest('[data-collection-banner-popup-close]') ||
          event.target.closest('[data-collection-banner-popup-backdrop]')
        ) {
          this.close();
        }
      }

      onChange(event) {
        const checkbox = event.target.closest('input[type="checkbox"][data-product-id]');
        if (!checkbox) return;
        const productId = checkbox.dataset.productId;
        if (checkbox.checked) {
          this.selected.add(productId);
        } else {
          this.selected.delete(productId);
        }
        this.updateCounter();
        this.updateTotal();
        this.updateCtaState();
      }

      onKeydown(event) {
        if (event.key === 'Escape') {
          if (this.isSetDropdownOpen()) {
            this.closeSetDropdown();
            this.querySelector('[data-popup-set-toggle]')?.focus();
          } else {
            this.close();
          }
          return;
        }
        if (event.key === 'Tab') {
          this.trapFocus(event);
        }
      }

      trapFocus(event) {
        const focusables = this.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        if (focusables.length === 0) return;
        const visible = Array.from(focusables).filter((el) => !el.closest('[hidden]'));
        if (visible.length === 0) return;
        const first = visible[0];
        const last = visible[visible.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }

      // ---- Open / Close ----

      open(triggerEl) {
        // triggerEl can be the card itself (data-trigger now on card) or any descendant
        const card =
          triggerEl.closest('.product-item--collection-banner') ||
          (triggerEl.classList?.contains('product-item--collection-banner') ? triggerEl : null);
        const template = card?.querySelector('template.collection-banner-popup__template');
        if (!template) {
          console.warn('[collection-banner-popup] template not found for trigger', triggerEl);
          return;
        }

        // Focus restore target — the button inside the card (focusable),
        // not the card div itself (not focusable)
        this.triggerButton = card.querySelector('.collection-banner__btn') || triggerEl;
        this.activeSetIdx = undefined;
        this.populate(template);

        if (this.sets.length === 0) {
          // Defensive: nothing to show
          this.triggerButton = null;
          return;
        }

        this.hidden = false;
        document.body.classList.add('collection-banner-popup-open');
        document.addEventListener('keydown', this.boundOnKeydown);

        this.selectSet(0, false);

        // Reset scroll to top — defensive for mobile (column flex with scroll
        // container can leave scrollTop in unexpected position on first show)
        const dialog = this.querySelector('.collection-banner-popup__dialog');
        if (dialog) dialog.scrollTop = 0;
        const content = this.querySelector('.collection-banner-popup__content');
        if (content) content.scrollTop = 0;
        const products = this.querySelector('[data-popup-products-target]');
        if (products) products.scrollTop = 0;

        const focusTarget = this.querySelector('[data-collection-banner-popup-close]');
        focusTarget?.focus({ preventScroll: true });
      }

      close() {
        this.closeSetDropdown();
        this.hidden = true;
        document.body.classList.remove('collection-banner-popup-open');
        document.removeEventListener('keydown', this.boundOnKeydown);

        const trigger = this.triggerButton;
        this.triggerButton = null;
        this.sets = [];
        this.activeSetIdx = undefined;
        this.selected = new Set();

        if (this.copyResetTimer) {
          clearTimeout(this.copyResetTimer);
          this.copyResetTimer = null;
        }
        if (this.toastTimer) {
          clearTimeout(this.toastTimer);
          this.toastTimer = null;
        }
        const toast = this.querySelector('[data-popup-toast]');
        if (toast) toast.hidden = true;

        trigger?.focus();
      }

      // ---- Populate from template ----

      populate(template) {
        const root = template.content.firstElementChild;
        if (!root) {
          this.sets = [];
          return;
        }
        const data = root.dataset;

        // Header
        this.querySelector('[data-popup-title-target]').textContent = data.popupTitle || '';
        this.querySelector('[data-popup-promo-target]').textContent = data.popupPromo || '';

        const discountEl = this.querySelector('[data-popup-discount-target]');
        const discountLabel = discountEl?.querySelector('[data-popup-discount-label]');
        const promoCodeEl = this.querySelector('[data-popup-promo-code-target]');
        if (data.popupDiscount) {
          if (promoCodeEl) {
            promoCodeEl.textContent = data.popupDiscount;
            promoCodeEl.hidden = false;
          }
          if (discountLabel) discountLabel.textContent = this.dataset.i18nCopy || 'Copy';
          discountEl.dataset.code = data.popupDiscount;
          discountEl.hidden = false;
        } else {
          if (promoCodeEl) {
            promoCodeEl.textContent = '';
            promoCodeEl.hidden = true;
          }
          if (discountLabel) discountLabel.textContent = '';
          discountEl.hidden = true;
          delete discountEl.dataset.code;
        }

        this.querySelector('[data-popup-cta-text]').textContent = data.popupCta || '';

        // Sets
        const setNodes = root.querySelectorAll('[data-set-name]');
        this.sets = Array.from(setNodes).map((node) => ({
          name: node.dataset.setName,
          subtitle: node.dataset.setSubtitle,
          title: node.dataset.setTitle,
          image: {
            src: node.dataset.setImageSrc,
            mobileSrc: node.dataset.setImageMobileSrc,
            alt: node.dataset.setImageAlt,
          },
          products: Array.from(node.querySelectorAll('[data-product-id]')).map((p) => ({
            productId: p.dataset.productId,
            variantId: p.dataset.variantId,
            title: p.dataset.productTitle,
            image: p.dataset.productImage,
            priceCents: parseInt(p.dataset.productPriceCents, 10) || 0,
          })),
        }));

        // Set selector visibility
        const setControl = this.querySelector('[data-popup-set-control]');
        const setOptions = this.querySelector('[data-popup-set-options]');
        if (this.sets.length > 1) {
          setControl.hidden = false;
          setOptions.innerHTML = this.sets
            .map(
              (s, i) => `
                <li
                  class="collection-banner-popup__set-option"
                  role="option"
                  data-set-idx="${i}"
                  aria-selected="${i === 0 ? 'true' : 'false'}"
                  tabindex="-1"
                >${this.escapeHtml(s.name)}</li>
              `
            )
            .join('');
        } else {
          setControl.hidden = true;
          setOptions.innerHTML = '';
        }
        // Reset dropdown state on every populate (new banner opened)
        this.closeSetDropdown();
      }

      // ---- Set selection ----

      selectSet(idx, animate = true) {
        if (idx < 0 || idx >= this.sets.length) return;
        const wasInitialized = this.activeSetIdx !== undefined;
        const set = this.sets[idx];

        // Update toggle label + aria-selected on options (immediately, no fade)
        const toggleLabel = this.querySelector('[data-popup-set-toggle-label]');
        if (toggleLabel) toggleLabel.textContent = set.name;
        this.querySelectorAll('[data-set-idx]').forEach((opt) => {
          opt.setAttribute('aria-selected', String(opt.dataset.setIdx === String(idx)));
        });

        const fadeTargets = [
          this.querySelector('[data-popup-products-target]'),
          this.querySelector('[data-popup-media-image]'),
          this.querySelector('.collection-banner-popup__media-overlay'),
        ];

        const apply = () => {
          this.activeSetIdx = idx;
          this.renderSet(set);
        };

        if (wasInitialized && animate) {
          fadeTargets.forEach((el) => el?.classList.add('is-fading'));
          setTimeout(() => {
            apply();
            requestAnimationFrame(() => {
              fadeTargets.forEach((el) => el?.classList.remove('is-fading'));
            });
          }, 150);
        } else {
          apply();
        }
      }

      renderSet(set) {
        // Media
        const img = this.querySelector('[data-popup-media-image]');
        const sourceMobile = this.querySelector('[data-popup-media-source-mobile]');
        if (sourceMobile) {
          if (set.image.mobileSrc) {
            sourceMobile.srcset = set.image.mobileSrc;
          } else {
            sourceMobile.removeAttribute('srcset');
          }
        }
        img.src = set.image.src || CollectionBannerPopup.PLACEHOLDER_IMG;
        img.alt = set.image.alt || '';
        this.querySelector('[data-popup-media-subtitle]').textContent = set.subtitle || '';
        this.querySelector('[data-popup-media-title]').textContent = set.title || '';

        // Products list
        const list = this.querySelector('[data-popup-products-target]');
        list.innerHTML = set.products
          .map(
            (p) => `
              <li class="collection-banner-popup__product">
                <label class="collection-banner-popup__product-label">
                  <input
                    type="checkbox"
                    class="collection-banner-popup__product-checkbox"
                    checked
                    data-product-id="${this.escapeHtml(p.productId)}"
                  >
                  <img
                    class="collection-banner-popup__product-image"
                    src="${this.escapeHtml(p.image || CollectionBannerPopup.PLACEHOLDER_IMG)}"
                    alt=""
                    loading="lazy"
                    width="60"
                    height="60"
                  >
                  <span class="collection-banner-popup__product-info">
                    <span class="collection-banner-popup__product-brand">D.LOUISE</span>
                    <span class="collection-banner-popup__product-title">${this.escapeHtml(p.title)}</span>
                    <span class="collection-banner-popup__product-price">${this.escapeHtml(this.formatMoney(p.priceCents))}</span>
                  </span>
                </label>
              </li>
            `
          )
          .join('');

        // Selected state — все по умолчанию
        this.selected = new Set(set.products.map((p) => p.productId));
        this.updateCounter();
        this.updateTotal();
        this.updateCtaState();
      }

      // ---- Reactive updates ----

      updateCounter() {
        const count = this.selected.size;
        const tmpl = count === 1 ? this.dataset.i18nPiecesOne : this.dataset.i18nPiecesOther;
        const text = (tmpl || '%count%').replace('%count%', count);
        this.querySelector('[data-popup-counter-target]').textContent = text;
      }

      updateTotal() {
        const set = this.sets[this.activeSetIdx];
        if (!set) return;
        let totalCents = 0;
        set.products.forEach((p) => {
          if (this.selected.has(p.productId)) totalCents += p.priceCents;
        });
        this.querySelector('[data-popup-total-target]').textContent = this.formatMoney(totalCents);
      }

      updateCtaState() {
        const cta = this.querySelector('[data-popup-cta-target]');
        cta.disabled = this.selected.size === 0;
      }

      // ---- Discount copy ----

      async copyDiscount() {
        const btn = this.querySelector('[data-popup-discount-target]');
        const label = btn?.querySelector('[data-popup-discount-label]');
        const code = btn.dataset.code;
        if (!code) return;

        try {
          await navigator.clipboard.writeText(code);
        } catch (e) {
          // Fallback for insecure contexts / older browsers
          try {
            const ta = document.createElement('textarea');
            ta.value = code;
            ta.setAttribute('readonly', '');
            ta.style.position = 'absolute';
            ta.style.left = '-9999px';
            document.body.appendChild(ta);
            ta.select();
            document.execCommand('copy');
            document.body.removeChild(ta);
          } catch (e2) {
            console.warn('[collection-banner-popup] clipboard copy failed', e2);
            return;
          }
        }

        if (this.copyResetTimer) clearTimeout(this.copyResetTimer);
        if (label) label.textContent = this.dataset.i18nCopied || 'Copied!';
        this.copyResetTimer = setTimeout(() => {
          if (label) label.textContent = this.dataset.i18nCopy || 'Copy';
          this.copyResetTimer = null;
        }, 1500);
      }

      // ---- Add to cart ----

      async addToCart() {
        const set = this.sets[this.activeSetIdx];
        if (!set) return;

        const items = set.products
          .filter((p) => this.selected.has(p.productId))
          .map((p) => ({ id: parseInt(p.variantId, 10), quantity: 1 }))
          .filter((it) => Number.isFinite(it.id));

        if (items.length === 0) return;

        const cta = this.querySelector('[data-popup-cta-target]');
        cta.disabled = true;
        cta.classList.add('is-loading');

        let success = true;
        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({ items }),
          });
          if (!response.ok) {
            success = false;
            const err = await response.json().catch(() => ({}));
            console.warn('[collection-banner-popup] cart/add error', err);
          }
        } catch (e) {
          success = false;
          console.warn('[collection-banner-popup] network error', e);
        } finally {
          cta.classList.remove('is-loading');
        }

        if (!success) {
          this.showToast(this.dataset.i18nCartError || 'Some items couldn\'t be added');
          // On error keep popup open so user can retry — don't trigger drawer
          return;
        }

        // Refresh cart contents (cart-items component listens on document)
        document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));

        // Close popup, then open cart drawer
        this.close();

        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) {
          cartDrawer.dispatchEvent(new CustomEvent('theme:cart-drawer:show'));
        }
      }

      // ---- Toast ----

      showToast(message, durationMs = 3000) {
        const toast = this.querySelector('[data-popup-toast]');
        if (!toast) return;
        toast.textContent = message;
        toast.hidden = false;
        if (this.toastTimer) clearTimeout(this.toastTimer);
        this.toastTimer = setTimeout(() => {
          toast.hidden = true;
          this.toastTimer = null;
        }, durationMs);
      }

      // ---- Set dropdown ----

      isSetDropdownOpen() {
        const options = this.querySelector('[data-popup-set-options]');
        return options ? !options.hidden : false;
      }

      openSetDropdown() {
        const options = this.querySelector('[data-popup-set-options]');
        const toggle = this.querySelector('[data-popup-set-toggle]');
        if (!options || !toggle) return;
        options.hidden = false;
        toggle.setAttribute('aria-expanded', 'true');
      }

      closeSetDropdown() {
        const options = this.querySelector('[data-popup-set-options]');
        const toggle = this.querySelector('[data-popup-set-toggle]');
        if (!options || !toggle) return;
        options.hidden = true;
        toggle.setAttribute('aria-expanded', 'false');
      }

      toggleSetDropdown() {
        if (this.isSetDropdownOpen()) {
          this.closeSetDropdown();
        } else {
          this.openSetDropdown();
        }
      }

      // ---- Helpers ----

      formatMoney(cents) {
        const amount = (cents || 0) / 100;
        const currency =
          (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) ||
          'USD';
        const locale =
          (window.Shopify && window.Shopify.locale) ||
          document.documentElement.lang ||
          'en';
        try {
          return new Intl.NumberFormat(locale, {
            style: 'currency',
            currency,
            currencyDisplay: 'narrowSymbol',
          }).format(amount);
        } catch (e) {
          return amount.toFixed(2);
        }
      }

      escapeHtml(str) {
        if (str == null) return '';
        const div = document.createElement('div');
        div.textContent = String(str);
        return div.innerHTML;
      }
    }
  );
}
